import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoadingSpinner from './components/LoadingSpinner';
import './App.css';

// Lazy load all page components for code splitting
const SignUp = lazy(() => import('./pages/SignUp'));
const SignIn = lazy(() => import('./pages/SignIn'));
const Profile = lazy(() => import('./pages/Profile'));
const Pricing = lazy(() => import('./pages/Pricing'));
const DemoPage = lazy(() => import('./pages/DemoPage'));
const Guides = lazy(() => import('./pages/Guides'));
const GuideDetail = lazy(() => import('./pages/GuideDetail'));
const Playground = lazy(() => import('./pages/Playground'));
const Agents = lazy(() => import('./pages/Agents'));
const AgentPlayground = lazy(() => import('./pages/AgentPlayground'));
const ThisMonth = lazy(() => import('./pages/ThisMonth'));
const HomePage = lazy(() => import('./pages/HomePage'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const Contact = lazy(() => import('./pages/Contact'));

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#050507' }}>
      <p style={{ color: '#888' }}>Loading...</p>
    </div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  return children;
};

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signout, isAuthenticated } = useAuth();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  const isPro = user && (user.subscription_tier === 'pro' || user.subscription_tier === 'lifetime');

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Setup Guides', path: '/guides' },
    { name: 'Prompts', path: '/playground' },
    { name: 'AI Agents', path: '/agents' },
    { name: 'This Month', path: '/this-month' },
  ];

  return (
    <>
      <header
        className={`sticky top-0 z-50 transition-all duration-500 ${
          isScrolled ? 'backdrop-blur-xl' : 'backdrop-blur-sm'
        }`}
        style={{
          backgroundColor: isScrolled ? 'rgba(5, 5, 7, 0.8)' : 'rgba(5, 5, 7, 0.5)',
          borderBottom: 'none'
        }}
        data-testid="main-header"
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 text-xl font-space font-bold" data-testid="logo-link">
            <span className="logo-key">Ctrl</span>
            <span className="gradient-text">+AI</span>
          </Link>

          {/* Desktop Navigation Tabs */}
          <div className="hidden lg:flex items-center gap-1" data-testid="nav-tabs">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-4 py-2 rounded-lg text-sm font-inter font-medium transition-all duration-200 ${
                  location.pathname === item.path
                    ? ''
                    : 'hover:bg-white/5'
                }`}
                style={{
                  color: location.pathname === item.path ? '#f0f0f0' : '#888',
                  backgroundColor: 'transparent'
                }}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg transition-colors"
              style={{ color: '#f0f0f0' }}
              data-testid="mobile-menu-button"
              aria-label="Toggle menu"
            >
              <span className="text-2xl">{mobileMenuOpen ? '✕' : '☰'}</span>
            </button>

            {!isAuthenticated ? (
              <div className="hidden sm:flex items-center gap-3">
                <button
                  onClick={() => navigate('/signin')}
                  data-testid="sign-in-button"
                  className="px-4 py-2 text-sm font-inter font-medium transition-colors"
                  style={{ color: '#888' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#f0f0f0'}
                  onMouseLeave={e => e.currentTarget.style.color = '#888'}
                >
                  Sign In
                </button>
                <button
                  onClick={() => navigate('/pricing')}
                  data-testid="get-pro-button"
                  className="px-5 py-2 text-sm font-inter font-semibold rounded-full transition-all"
                  style={{ backgroundColor: '#f0f0f0', color: '#050507' }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                >
                  Get Pro
                </button>
              </div>
            ) : (
              <div className="relative hidden sm:flex items-center gap-2" ref={dropdownRef}>
                {isPro && (
                  <span className="px-2 py-0.5 text-xs rounded-full font-semibold" style={{ backgroundColor: 'rgba(251, 191, 36, 0.2)', color: '#fbbf24' }}>
                    PRO
                  </span>
                )}
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  data-testid="user-menu-button"
                >
                  {user.avatar_url ? (
                    <img
                      src={user.avatar_url}
                      alt=""
                      className="w-10 h-10 rounded-full"
                      referrerPolicy="no-referrer"
                      data-testid="user-avatar-header"
                    />
                  ) : (
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-space font-bold"
                      style={{ backgroundColor: '#333', color: '#fff' }}
                      data-testid="user-avatar-header"
                    >
                      {user.full_name.charAt(0).toUpperCase()}
                    </div>
                  )}
                </button>

                {showDropdown && (
                  <div
                    className="absolute top-full right-0 mt-2 w-48 rounded-xl py-2 shadow-lg z-50 backdrop-blur-xl"
                    style={{ backgroundColor: 'rgba(10, 10, 16, 0.95)', border: '1px solid rgba(255,255,255,0.06)' }}
                    data-testid="user-dropdown"
                  >
                    <Link
                      to="/profile"
                      onClick={() => setShowDropdown(false)}
                      data-testid="dropdown-profile"
                      className="block px-4 py-2 text-sm font-inter transition-colors hover:bg-white/5"
                      style={{ color: '#f0f0f0' }}
                    >
                      Profile
                    </Link>
                    <button
                      onClick={() => {
                        setShowDropdown(false);
                        navigate('/profile');
                        setTimeout(() => {
                          const subscriptionSection = document.getElementById('subscription-section');
                          if (subscriptionSection) {
                            subscriptionSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }
                        }, 100);
                      }}
                      data-testid="dropdown-subscription"
                      className="w-full text-left px-4 py-2 text-sm font-inter transition-colors hover:bg-white/5"
                      style={{ color: '#f0f0f0' }}
                    >
                      Subscription
                    </button>
                    <button
                      onClick={() => {
                        setShowDropdown(false);
                        signout();
                        navigate('/');
                      }}
                      data-testid="dropdown-signout"
                      className="w-full text-left px-4 py-2 text-sm font-inter transition-colors hover:bg-white/5"
                      style={{ color: '#f0f0f0' }}
                    >
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </nav>
      </header>

      {/* Mobile Slide-out Menu */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-opacity duration-300 ${
          mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        onClick={() => setMobileMenuOpen(false)}
      />
      <div
        className={`fixed top-0 right-0 h-full w-72 z-50 lg:hidden transform transition-transform duration-300 ${
          mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ backgroundColor: 'rgba(10, 10, 16, 0.98)', borderLeft: '1px solid rgba(255,255,255,0.06)' }}
        data-testid="mobile-menu"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-8">
            <span className="font-space font-bold text-xl" style={{ color: '#f0f0f0' }}>Menu</span>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="p-2"
              style={{ color: '#888' }}
            >
              ✕
            </button>
          </div>

          <nav className="space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="block px-4 py-3 rounded-xl font-inter font-medium transition-all hover:bg-white/5"
                style={{
                  backgroundColor: location.pathname === item.path ? 'rgba(255,255,255,0.05)' : 'transparent',
                  color: location.pathname === item.path ? '#f0f0f0' : '#888'
                }}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          <div className="mt-8 pt-8 space-y-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            {!isAuthenticated ? (
              <>
                <Link
                  to="/signin"
                  className="block w-full py-3 text-center font-inter font-medium rounded-full transition-all"
                  style={{ border: '1px solid rgba(255,255,255,0.15)', color: '#f0f0f0' }}
                >
                  Sign In
                </Link>
                <Link
                  to="/pricing"
                  className="block w-full py-3 text-center font-inter font-semibold rounded-full"
                  style={{ backgroundColor: '#f0f0f0', color: '#050507' }}
                >
                  Get Pro
                </Link>
              </>
            ) : (
              <>
                <Link
                  to="/profile"
                  className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all hover:bg-white/5"
                >
                  {user.avatar_url ? (
                    <img src={user.avatar_url} alt="" className="w-10 h-10 rounded-full" referrerPolicy="no-referrer" />
                  ) : (
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-space font-bold"
                      style={{ backgroundColor: '#333', color: '#fff' }}
                    >
                      {user.full_name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <div className="font-inter font-medium" style={{ color: '#f0f0f0' }}>{user.full_name}</div>
                    {isPro && (
                      <span className="text-xs" style={{ color: '#fbbf24' }}>PRO Member</span>
                    )}
                  </div>
                </Link>
                <button
                  onClick={() => {
                    signout();
                    navigate('/');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full py-3 text-center font-inter font-medium rounded-full transition-all"
                  style={{ border: '1px solid rgba(255,255,255,0.15)', color: '#f0f0f0' }}
                >
                  Sign Out
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="mt-20 relative" style={{ backgroundColor: '#050507' }} data-testid="footer">
      <div style={{ height: '1px', backgroundColor: 'rgba(255,255,255,0.06)' }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 text-xl font-space font-bold mb-4">
            <span className="logo-key">Ctrl</span>
            <span style={{ color: '#f0f0f0' }}>+AI</span>
          </div>
          <p className="font-inter text-sm mb-2" style={{ color: '#888' }}>The shortcut to mastering AI.</p>
          <p className="font-inter text-sm" style={{ color: '#555' }}>Ctrl+AI &copy; 2026</p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 pt-8" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <Link to="/privacy" data-testid="privacy-link" className="text-sm font-inter transition-colors hover:text-white" style={{ color: '#555' }}>
            Privacy Policy
          </Link>
          <span className="hidden sm:inline" style={{ color: '#333' }}>&middot;</span>
          <Link to="/terms" data-testid="terms-link" className="text-sm font-inter transition-colors hover:text-white" style={{ color: '#555' }}>
            Terms of Service
          </Link>
          <span className="hidden sm:inline" style={{ color: '#333' }}>&middot;</span>
          <Link to="/contact" data-testid="contact-link" className="text-sm font-inter transition-colors hover:text-white" style={{ color: '#555' }}>
            Contact
          </Link>
        </div>
      </div>
    </footer>
  );
};


// Placeholder Pages
const PlaceholderPage = ({ title }) => {
  return (
    <div className="max-w-4xl mx-auto px-6 py-20 text-center" data-testid="placeholder-page">
      <h1 className="text-5xl font-space font-bold mb-6" style={{ color: '#f0f0f0' }}>{title}</h1>
      <p className="text-xl font-inter mb-8" style={{ color: '#888' }}>This page is coming soon!</p>
      <Link
        to="/"
        className="inline-block px-8 py-4 text-lg font-inter font-semibold rounded-full transition-all"
        style={{ backgroundColor: '#f0f0f0', color: '#050507' }}
      >
        Back to Home
      </Link>
    </div>
  );
};

// 404 Not Found Page
const NotFoundPage = () => {
  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: '#050507' }} data-testid="not-found-page">
      <div className="text-center">
        <div className="text-8xl mb-6">🔍</div>
        <h1 className="text-6xl font-space font-bold mb-4" style={{ color: '#f0f0f0' }}>404</h1>
        <h2 className="text-3xl font-space font-bold mb-4" style={{ color: '#f0f0f0' }}>Page Not Found</h2>
        <p className="text-xl font-inter mb-8 max-w-md" style={{ color: '#888' }}>
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="flex gap-4 justify-center">
          <Link
            to="/"
            className="px-8 py-4 rounded-full font-inter font-semibold text-lg transition-all"
            style={{ backgroundColor: '#f0f0f0', color: '#050507' }}
          >
            Go Home
          </Link>
          <Link
            to="/guides"
            className="px-8 py-4 rounded-full font-inter font-semibold text-lg transition-all"
            style={{ border: '1px solid rgba(255,255,255,0.15)', color: '#f0f0f0' }}
          >
            Explore Guides
          </Link>
        </div>
      </div>
    </div>
  );
};

// Layout wrapper — hides Header/Footer on auth pages
const AppLayout = () => {
  const location = useLocation();
  const isAuthPage = ['/signin', '/signup'].includes(location.pathname);

  return (
    <>
      <Toaster position="top-center" richColors visibleToasts={1} duration={3000} toastOptions={{ style: { marginTop: isAuthPage ? '20px' : '60px', zIndex: 9999 } }} />
      {!isAuthPage && <Header />}
      <main>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/demo" element={<DemoPage />} />
            <Route path="/guides" element={<Guides />} />
            <Route path="/guides/:slug" element={<GuideDetail />} />
            <Route path="/playground" element={<Playground />} />
            <Route path="/templates" element={<Navigate to="/playground?tab=library" replace />} />
            <Route path="/techniques" element={<Navigate to="/playground?tab=gym" replace />} />
            <Route path="/agents" element={<Agents />} />
            <Route path="/agent-playground" element={<AgentPlayground />} />
            <Route path="/this-month" element={<ThisMonth />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/calculator" element={<PlaceholderPage title="AI Platform Comparison" />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      {!isAuthPage && <Footer />}
    </>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppLayout />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
